import {
  orderSourcingSchema,
  sourcingRequirementPageSchema,
  sourcingRequirementSchema,
  type CreateSourcingRequirementInput,
  type OrderSourcing,
  type SetSourcingStatusInput,
  type SourcingRequirement,
  type SourcingRequirementPage,
  type SourcingStatus,
  type SourcingType,
  type UpdateSourcingRequirementInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every sourcing call the app makes.
 *
 * Note which verb is NOT here: there is nothing that raises a purchase order. A sourcing
 * requirement records what an order needs; buying the material from a supplier is the purchasing
 * module's document and lives under `lib/purchasing`. The two are deliberately separate calls
 * against separate resources, because they answer separate questions.
 *
 * Every response is parsed through the shared schema rather than cast, so a server that changed
 * shape fails here — at the edge, with the URL in the message — rather than three components later
 * as an undefined property.
 */

const SOURCING = '/sourcing';

export interface SourcingSearchParams {
  q?: string;
  /** Repeat for several: the screen opens on `to_start` and `in_progress` together. */
  status?: readonly SourcingStatus[];
  type?: SourcingType;
  orderId?: string;
  page?: number;
  pageSize?: number;
}

/** Everything still to be got, newest first. What the sourcing screen reads. */
export async function searchSourcing(
  client: ApiClient,
  params: SourcingSearchParams = {},
): Promise<SourcingRequirementPage> {
  return await client.get(SOURCING, {
    schema: sourcingRequirementPageSchema,
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.status?.length ? { status: [...params.status] } : {}),
      ...(params.type ? { type: params.type } : {}),
      ...(params.orderId ? { orderId: params.orderId } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

/** One order's needs and what they come to. What the card on the order detail page reads. */
export async function getOrderSourcing(client: ApiClient, orderId: string): Promise<OrderSourcing> {
  return await client.get(`${SOURCING}/orders/${orderId}`, { schema: orderSourcingSchema });
}

export async function getSourcingRequirement(
  client: ApiClient,
  sourcingId: string,
): Promise<SourcingRequirement> {
  return await client.get(`${SOURCING}/${sourcingId}`, { schema: sourcingRequirementSchema });
}

export async function createSourcingRequirement(
  client: ApiClient,
  input: CreateSourcingRequirementInput,
): Promise<SourcingRequirement> {
  return await client.post(SOURCING, input, { schema: sourcingRequirementSchema });
}

/** A row that is still being worked. The API refuses a `received` or `cancelled` one. */
export async function updateSourcingRequirement(
  client: ApiClient,
  sourcingId: string,
  patch: UpdateSourcingRequirementInput,
): Promise<SourcingRequirement> {
  return await client.patch(`${SOURCING}/${sourcingId}`, patch, {
    schema: sourcingRequirementSchema,
  });
}

/** One step along the lifecycle. Anything the shared table forbids comes back a 409. */
export async function setSourcingStatus(
  client: ApiClient,
  sourcingId: string,
  input: SetSourcingStatusInput,
): Promise<SourcingRequirement> {
  return await client.post(`${SOURCING}/${sourcingId}/status`, input, {
    schema: sourcingRequirementSchema,
  });
}

/** Only while nobody has started on it — anything further along is cancelled, not erased. */
export async function deleteSourcingRequirement(
  client: ApiClient,
  sourcingId: string,
): Promise<void> {
  await client.del(`${SOURCING}/${sourcingId}`);
}
